import { clsx } from 'clsx';

export interface TicketTabDef {
  key: string;
  label: string;
  count?: number;
}

/**
 * Ivory & Brass tab strip.
 *
 * Mono caps, 2px brass bottom-border on the active tab, count in brass beside
 * the label. Replaces the blue-600 sentence-case tabs inline in
 * TicketDetailPage — pulled out because that file had four near-identical
 * 6-line className ternaries for what is one component.
 */
export function TicketTabs({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: TicketTabDef[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={clsx(
        'flex flex-shrink-0 items-center gap-[2px] border-b border-[var(--co-line)] px-4',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={clsx(
              'relative -mb-px border-b-2 px-[18px] pb-[12px] pt-[14px] text-[14.5px] font-medium leading-none transition-colors duration-[120ms]',
              isActive
                ? 'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
                : 'border-transparent text-[var(--co-ink-3)] hover:text-[var(--co-ink)]',
            )}
          >
            {tab.label}
            {typeof tab.count === 'number' && tab.count > 0 && (
              <span
                className={clsx(
                  'ml-[6px] rounded-full px-1.5 py-0.5 text-[11px]',
                  isActive
                    ? 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]'
                    : 'bg-[var(--co-canvas)] text-[var(--co-ink-4)]',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default TicketTabs;
